/**
 * Register Component
 * Responsive layout: mobile-first, same UX as Login (gradient hero + form).
 */

import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';

export default function Register() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const { t } = useTranslation();

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    phone: '',
    username: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await register({
        email: formData.email,
        password: formData.password,
        first_name: formData.first_name,
        last_name: formData.last_name,
        phone: formData.phone || undefined,
        username: formData.username || undefined
      });
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.detail || t('register.errorDefault') || 'Registration failed.');
    } finally {
      setLoading(false);
    }
  };

  const heroTitle = t('register.heroTitle') || 'AI Crawler Cloud';
  const heroSubtitle = t('register.heroSubtitle') || 'Create your account';
  const title = t('register.title') || 'Create account';
  const subtitle = t('register.subtitle') || 'Fill in your details to get started.';

  return (
    <div className="flex flex-col min-h-screen md:flex-row md:h-screen overflow-hidden">
      {/* Mobile-only header */}
      <header
        className="sm:hidden flex items-center justify-center gap-3 py-4 px-4 shrink-0"
        style={{ background: 'linear-gradient(135deg, #007E8F 0%, #00C6B3 100%)' }}
      >
        <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
          <i className="fa-solid fa-robot text-white text-xl"></i>
        </div>
        <div>
          <h1 className="text-lg font-bold text-white">{heroTitle}</h1>
          <p className="text-white/80 text-xs">{heroSubtitle}</p>
        </div>
      </header>

      {/* Hero - hidden on xs */}
      <section
        className="hidden sm:flex flex-1 flex-col items-center justify-center p-4 sm:p-6 md:p-12 relative overflow-hidden min-h-[200px] md:min-h-0"
        style={{ background: 'linear-gradient(135deg, #007E8F 0%, #00C6B3 50%, #6FFFE0 100%)' }}
      >
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <div className="absolute top-10 left-10 w-32 h-32 md:top-20 md:left-20 md:w-64 md:h-64 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-48 h-48 md:bottom-20 md:right-20 md:w-96 md:h-96 bg-white rounded-full blur-3xl"></div>
        </div>
        <div className="relative z-10 max-w-xl w-full text-center sm:text-left">
          <div className="mb-6 md:mb-12">
            <div className="flex items-center justify-center sm:justify-start space-x-3 sm:space-x-4 mb-4">
              <div className="w-14 h-14 sm:w-16 sm:h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center shrink-0">
                <i className="fa-solid fa-user-plus text-white text-2xl sm:text-3xl"></i>
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-white">{heroTitle}</h1>
                <p className="text-white/80 text-sm">{heroSubtitle}</p>
              </div>
            </div>
          </div>
          <p className="text-white/90 text-sm sm:text-base md:text-lg">
            {t('register.heroDescription') || 'Join and use AI-powered extraction, templates, and crawler monitoring.'}
          </p>
        </div>
      </section>

      {/* Form panel */}
      <section className="w-full md:w-[600px] md:min-w-[400px] md:max-w-[600px] flex-1 flex items-center justify-center bg-white shadow-2xl overflow-y-auto md:shrink-0">
        <div className="w-full max-w-md py-6 px-4 sm:py-8 sm:px-6 md:py-12 md:px-8">
          <div className="mb-6 sm:mb-8 md:mb-10 text-center">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#212121] mb-2 md:mb-3">{title}</h2>
            <p className="text-[#4A4A4A] text-sm sm:text-base md:text-lg">{subtitle}</p>
          </div>

          {error && (
            <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-red-50 border-2 border-red-200 rounded-xl">
              <div className="flex items-center">
                <i className="fas fa-exclamation-circle text-red-600 mr-3 shrink-0"></i>
                <p className="text-sm text-red-600 font-medium">{error}</p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5">
            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              <div>
                <label className="block mb-1.5">
                  <span className="text-sm font-bold text-[#212121]">{t('register.firstName') || 'First name'} <span className="text-[#00C6B3]">*</span></span>
                </label>
                <input
                  type="text"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                  required
                  className="w-full pl-3 pr-4 py-3 sm:py-3.5 border-2 border-[#B0B9C1]/30 rounded-xl focus:ring-2 focus:ring-[#00C6B3] focus:border-transparent text-[#212121] text-base min-h-[48px]"
                />
              </div>
              <div>
                <label className="block mb-1.5">
                  <span className="text-sm font-bold text-[#212121]">{t('register.lastName') || 'Last name'} <span className="text-[#00C6B3]">*</span></span>
                </label>
                <input
                  type="text"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  required
                  className="w-full pl-3 pr-4 py-3 sm:py-3.5 border-2 border-[#B0B9C1]/30 rounded-xl focus:ring-2 focus:ring-[#00C6B3] focus:border-transparent text-[#212121] text-base min-h-[48px]"
                />
              </div>
            </div>

            <div>
              <label className="block mb-1.5">
                <span className="text-sm font-bold text-[#212121]">{t('login.email') || 'Email'} <span className="text-[#00C6B3]">*</span></span>
              </label>
              <div className="relative">
                <i className="fa-solid fa-envelope absolute left-3 top-1/2 -translate-y-1/2 text-[#B0B9C1]"></i>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="your.email@example.com"
                  required
                  className="w-full pl-11 pr-4 py-3 sm:py-3.5 border-2 border-[#B0B9C1]/30 rounded-xl focus:ring-2 focus:ring-[#00C6B3] focus:border-transparent text-[#212121] text-base min-h-[48px]"
                />
              </div>
            </div>

            <div>
              <label className="block mb-1.5">
                <span className="text-sm font-bold text-[#212121]">{t('login.password') || 'Password'} <span className="text-[#00C6B3]">*</span></span>
              </label>
              <div className="relative">
                <i className="fa-solid fa-lock absolute left-3 top-1/2 -translate-y-1/2 text-[#B0B9C1]"></i>
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  minLength={8}
                  className="w-full pl-11 pr-12 py-3 sm:py-3.5 border-2 border-[#B0B9C1]/30 rounded-xl focus:ring-2 focus:ring-[#00C6B3] focus:border-transparent text-[#212121] text-base min-h-[48px]"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#B0B9C1] hover:text-[#00C6B3] p-1 touch-manipulation"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  <i className={`fa-solid ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                </button>
              </div>
            </div>

            <div>
              <label className="block mb-1.5">
                <span className="text-sm font-bold text-[#212121]">{t('register.phone') || 'Phone'}</span>
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="w-full pl-3 pr-4 py-3 sm:py-3.5 border-2 border-[#B0B9C1]/30 rounded-xl focus:ring-2 focus:ring-[#00C6B3] focus:border-transparent text-[#212121] text-base min-h-[48px]"
              />
            </div>

            <div>
              <label className="block mb-1.5">
                <span className="text-sm font-bold text-[#212121]">{t('register.username') || 'Username'}</span>
              </label>
              <input
                type="text"
                name="username"
                value={formData.username}
                onChange={handleChange}
                className="w-full pl-3 pr-4 py-3 sm:py-3.5 border-2 border-[#B0B9C1]/30 rounded-xl focus:ring-2 focus:ring-[#00C6B3] focus:border-transparent text-[#212121] text-base min-h-[48px]"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3.5 sm:py-4 min-h-[48px] text-white rounded-xl text-base sm:text-lg font-bold shadow-lg transition-all hover:opacity-90 active:scale-[0.98] flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
              style={{ background: 'linear-gradient(135deg, #007E8F 0%, #00C6B3 100%)' }}
            >
              <i className="fa-solid fa-user-plus mr-2 sm:mr-3"></i>
              {loading ? (t('register.creating') || 'Creating account…') : (t('register.submit') || 'Create account')}
            </button>
          </form>

          <div className="mt-6 sm:mt-8 text-center">
            <p className="text-[#4A4A4A] text-sm sm:text-base">
              {t('register.hasAccount') || 'Already have an account?'}{' '}
              <Link to="/login" className="font-bold text-[#00C6B3] hover:text-[#007E8F] transition-colors underline focus:outline-none focus:ring-2 focus:ring-[#00C6B3] rounded">
                {t('login.signIn') || 'Sign in'}
              </Link>
            </p>
          </div>

          <div className="mt-6 sm:mt-10 p-3 sm:p-4 bg-[#F5F6F8] rounded-xl border border-[#B0B9C1]/20">
            <div className="flex items-center justify-center gap-2 sm:gap-3 text-sm text-[#4A4A4A]">
              <i className="fa-solid fa-shield-halved text-[#00C6B3] shrink-0"></i>
              <span>{t('login.securityBadge') || 'Your data is stored securely.'}</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
